import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, alpha } from '@mui/material/styles';
import {
    Box,
    Link,
    Drawer,
    Typography,
    Avatar,
    IconButton,
    Tooltip
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
// components
import Logo from '../../../components/logo';
import Scrollbar from '../../../components/scrollbar';
import NavSection from '../../../components/nav-section';
import NavContactSupport from '../../../components/nav-support/NavContactSupport';
import { NavApi } from '../../../components/nav-api/NavApi';
// config
import config from '../../../config';
// context
import { useStateSelector } from '../../../context/StateSelectors';
// i18n
import i18n from '../../../i18n';
// lodash
import { cloneDeep } from 'lodash';
// ----------------------------------------------------------------------

const NAV_WIDTH = 280;
const COLLAPSED_WIDTH = 66;


const StyledAccount = styled('div')(({ theme, sx }) => ({
    ...sx,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2, 2.5),
    borderRadius: Number(theme.shape.borderRadius) * 1.5,
    backgroundColor: alpha(theme.palette.grey[500], 0.12),
    transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shortest
    })
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
    openNav: PropTypes.bool,
    onCloseNav: PropTypes.func
};

export default function Nav({ openNav, onCloseNav, collapsed, onToggleCollapse }) {
    const { pathname } = useLocation();
    const isDesktop = useResponsive('up', 'lg');
    const isLogo = config.logo ? config.logo : null;

    const firstName =
        useStateSelector((state) => state.userInfo?.firstName) || '';
    const lastName =
        useStateSelector((state) => state.userInfo?.lastName) || '';
    const role = useStateSelector((state) => state.userInfo?.role?.name) || '';
    const perms = useStateSelector((state) => state.permissions) || {};

    const [permissions, setPermissions] = useState({});

    const DRAWER_WIDTH = collapsed ? COLLAPSED_WIDTH : NAV_WIDTH;


    const handleNavClose = useCallback(() => {
        onCloseNav();
    }, [onCloseNav]);

    useEffect(() => {
        if (Object.keys(perms).length) {
            setPermissions(perms);
        }
    }, [perms]);

    useEffect(() => {
        if (openNav) {
            handleNavClose();
        }
    }, [pathname]);

    useEffect(() => {
        if (!isDesktop && openNav && collapsed) {
            onToggleCollapse();
        }
    }, [isDesktop, openNav, collapsed, onToggleCollapse]);

    const hasReadPermission = (permissionKey) => {
        return (
            permissions[permissionKey] &&
            permissions[permissionKey].read !== 'deny'
        );
    };

    const filterMenuItems = useCallback(
        (menu) => {
            const navMenu = cloneDeep(menu);
            if (!permissions || !Object.keys(permissions).length) return [];
            return navMenu.filter((menuItem) => {
                if (!menuItem.controller && !menuItem.sublistData) return true;
                if (menuItem.sublistData) {
                    menuItem.sublistData = filterMenuItems(
                        menuItem.sublistData
                    );
                    return menuItem.sublistData.length > 0;
                }
                return hasReadPermission(menuItem.controller);
            });
        },
        [permissions]
    );

    const navConfig = filterMenuItems(config.menu);

    const renderContent = (
        <Scrollbar
            sx={{
                "height": 1,
                '& .simplebar-content': {
                    height: 1,
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2.5,
                    py: 1.5
                }}
            >
                {isLogo && (
                    <Logo
                        collapsed={collapsed}
                        toggleCollapse={onToggleCollapse}
                    />
                )}
                {isDesktop && !isLogo && (
                    <IconButton onClick={onToggleCollapse}>
                        {collapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                )}
            </Box>

            <Box sx={{ mb: 3, mx: 2.5 }}>
                <Link underline="none">
                    <StyledAccount
                        sx={
                            collapsed
                                ? {
                                    padding: '16px 0',
                                    backgroundColor: 'transparent'
                                }
                                : {}
                        }
                    >
                        {collapsed ? (
                            <Tooltip
                                title={`${firstName} ${lastName}`}
                                placement="right"
                            >
                                <Avatar alt="AB" />
                            </Tooltip>
                        ) : (
                            <Avatar alt="AB" />
                        )}
                        {collapsed ? null : (
                            <Box sx={{ ml: 2 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ color: 'text.primary' }}
                                >
                                    {`${firstName} ${lastName}`}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: 'text.secondary' }}
                                >
                                    {role ? i18n.t(`enums.${role}`) : ''}
                                </Typography>
                            </Box>
                        )}
                    </StyledAccount>
                </Link>
            </Box>

            <NavSection
                collapsed={collapsed}
                data={navConfig.map((navEl) => {
                    if (navEl.sublistData && navEl.sublistData.length === 1) {
                        return {
                            ...navEl.sublistData[0],
                            titleKey: navEl.titleKey,
                            icon: navEl.icon
                        };
                    }
                    return navEl;
                })}
            />

            <Box sx={{ mt: 5 }}>
                <NavContactSupport collapsed={collapsed} />
                <NavApi collapsed={collapsed} />
            </Box>

            <Box sx={{ flexGrow: 1 }} />
        </Scrollbar>
    );

    return (
        <Box
            component="nav"
            sx={{
                flexShrink: { lg: 0 },
                width: { lg: DRAWER_WIDTH }
            }}
        >
            {isDesktop ? (
                <Drawer
                    open
                    variant="permanent"
                    PaperProps={{
                        sx: {
                            width: DRAWER_WIDTH,
                            bgcolor: 'background.default',
                            borderRightStyle: 'dashed',
                            transition: (theme) =>
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration:
                                        theme.transitions.duration.standard
                                })
                        }
                    }}
                >
                    {renderContent}
                </Drawer>
            ) : (
                <Drawer
                    open={openNav}
                    onClose={onCloseNav}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: { width: NAV_WIDTH }
                    }}
                >
                    {renderContent}
                </Drawer>
            )}
        </Box>
    );
}
